import { useGetBlobSASTokenMutation } from "features/api/apiSlice";
import { useEffect, useMemo, useState } from "react";
import { Alert, CircularProgress, Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IUser, UserProviderId } from "models/IUser";
import { Stack } from "@mui/system";
import { EmptyTable } from "components/third-party/ReactTable";
import { Column, Row, useTable } from "react-table";
import moment from "moment";
import MainCard from "components/MainCard";

interface Props {
  patientId: string;
  users: IUser[];
}

type SessionRow = Record<string, string>;

const sessionCsvCache = new Map<string, SessionRow[]>();

export function SessionSummary({ patientId, users }: Props) {
  const { t } = useTranslation();
  const user = users?.find((x) => x.id === patientId);

  const formatDurationValue = (value: string): string => {
    const trimmedValue = value?.trim();
    if (!trimmedValue) return value;

    const match = /^(?:(\d+)\s+days?\s+)?(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?$/.exec(trimmedValue);
    if (!match) return value;

    const days = Number(match[1] || 0);
    const hours = Number(match[2] || 0);
    const minutes = Number(match[3] || 0);
    const seconds = Number(match[4] || 0);
    const fractionalSeconds = match[5] ? Number(`0.${match[5]}`) : 0;

    const totalMilliseconds =
      (((days * 24 + hours) * 60 + minutes) * 60 + seconds + fractionalSeconds) * 1000;

    if (totalMilliseconds < 1000) {
      return `${Math.round(totalMilliseconds)} ms`;
    }

    const units = [
      { label: "d", value: 24 * 60 * 60 * 1000 },
      { label: "h", value: 60 * 60 * 1000 },
      { label: "min", value: 60 * 1000 },
      { label: "s", value: 1000 },
    ];

    let remainder = Math.floor(totalMilliseconds);
    const parts: string[] = [];

    for (const unit of units) {
      const amount = Math.floor(remainder / unit.value);
      if (amount > 0) {
        parts.push(`${amount} ${unit.label}`);
        remainder -= amount * unit.value;
      }

      if (parts.length === 2) break;
    }

    if (parts.length > 0) {
      return parts.join(" ");
    }

    return `${Math.max(1, Math.floor(totalMilliseconds / 1000))} s`;
  };

  const [sessionData, setSessionData] = useState<SessionRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [getBlobSASToken] = useGetBlobSASTokenMutation();

  useEffect(() => {
    const abortController = new AbortController();
    let isCancelled = false;

    async function loadCsv() {
      if (!user?.id) {
        setSessionData([]);
        return;
      }

      const blobName = `${user.id}.csv`;
      const cachedData = sessionCsvCache.get(blobName);
      if (cachedData) {
        setError(null);
        setSessionData(cachedData);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const getBlobSASTokenResponse = await getBlobSASToken({
          containerName: UserProviderId.VETLLAR.toLowerCase(),
          getBlobSasToken: { BlobName: blobName },
        }).unwrap();

        const response = await fetch(getBlobSASTokenResponse.data.url, { signal: abortController.signal });
        if (!response.ok) {
          throw new Error(`Failed to fetch CSV: ${response.statusText}`);
        }

        const csvText = await response.text();
        const lines = csvText.split("\n").filter((line) => line.trim().length > 0);
        if (lines.length === 0) {
          throw new Error("CSV file is empty");
        }

        const headers = lines[0].split(",").map((h) => h.trim());
        const data: SessionRow[] = [];

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(",").map((v) => v.trim());
          const row: SessionRow = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });
          data.push(row);
        }

        if (isCancelled) return;

        sessionCsvCache.set(blobName, data);
        setSessionData(data);
      } catch (error) {
        if (abortController.signal.aborted || isCancelled) return;

        console.error("Error loading CSV:", error);
        setError(error instanceof Error ? error.message : "An unknown error occurred");
        setSessionData([]);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    loadCsv();

    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [user?.id, getBlobSASToken]);

  const columns = useMemo(
    () =>
      Object.keys(sessionData[0] || {}).map((key) => {
        const normalizedKey = key.toLowerCase();
        const isDateColumn = normalizedKey === "start" || normalizedKey === "end";
        const isDurationColumn = normalizedKey === "duration";

        return {
          Header: key,
          accessor: key,
          disableSortBy: true,
          ...(isDateColumn && {
            Cell: ({ value }: { value: string }) => {
              const formattedValue = moment(value).isValid() ? moment(value).locale("es").format("l LTS") : value;

              return <Typography>{formattedValue}</Typography>;
            },
          }),
          ...(isDurationColumn && {
            Cell: ({ value }: { value: string }) => {
              return <Typography>{formatDurationValue(value)}</Typography>;
            },
          }),
        };
      }),
    [sessionData]
  );

  const tableData = useMemo(() => sessionData as object[], [sessionData]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable(
    {
      columns: columns as Column<object>[],
      data: tableData,
      autoResetPage: false,
    }
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {t("error", "Error")}: {error}
      </Alert>
    );
  }

  if (!sessionData.length) {
    return (
      <Alert severity="info" sx={{ my: 2 }}>
        {t("noData", "No data available")}
      </Alert>
    );
  }

  return (
    <MainCard content={false}>
      <Stack spacing={3}>
        <Table {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <TableCell {...column.getHeaderProps([{ className: column.className }])}>
                    {column.render("Header")}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {rows.length > 0 ? (
              rows.map((row: Row<object>) => {
                prepareRow(row);
                const rowProps = row.getRowProps();
                const { key, ...restRowProps } = rowProps;

                return (
                  <TableRow key={key} {...restRowProps} sx={{ bgcolor: "inherit" }}>
                    {row.cells.map((cell) => (
                      <TableCell {...cell.getCellProps([{ className: cell.column.className }])}>{cell.render("Cell")}</TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <EmptyTable msg={t("no_data_found", "Data not found")} colSpan={columns.length || 1} />
            )}
          </TableBody>
        </Table>
      </Stack>
    </MainCard>
  );
}
